import { ReactNode, memo } from 'react';
import * as React from 'react';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import { ChatBubbleLeftRightIcon, LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { GoogleRestworldRatingUI } from '../../../legacy';
import { HouseCheckIcon } from '@restworld/utils-common';
import { Heading, Text, Button, Link } from '@restworld/ui-ds-base';
import { useAnalytics } from '@restworld/utils-common';
import { useGlobalsService } from '@restworld/data-services';

import { ApplicationWizardUI } from './application-wizard-ui';
import { UploadCVUI } from './upload-cv-ui';
import { jobPositionApiDataAtom, restworldGoogleRatingDataAtom } from '../../../job-position/job-position.atoms';
import { activeTabAtom, spontaneousApplyDataAtom } from '../../worker-entry-point.atoms';
import { buildLoginUrlWithApplyIntent } from '../../apply-intent';
import { loginClickedProperties } from '../../existing-account-events';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IEntryComponentProps {
  hideBackBtn?: boolean;
}
export const EntryComponentNew: React.FunctionComponent<IEntryComponentProps> = ({ hideBackBtn }) => (
  // The EntryPointChecklistProvider is now lifted to WorkerEntryPointModal so the header + footer
  // share the context; here we just render the active screen.
  <MainContent hideBackBtn={hideBackBtn} />
);
export default EntryComponentNew;
const MainContent = memo(({ hideBackBtn }: { hideBackBtn?: boolean }) => {
  const activeTab = useAtomValue(activeTabAtom);
  if (activeTab === 'application-wizard') return <ApplicationWizardUI hideBackBtn={hideBackBtn} />;
  if (activeTab === 'upload-cv') return <UploadCVUI hideBackBtn={hideBackBtn} />;
  return <EntryPointUI />;
});
const EntryPointUI = memo(() => {
  const { t } = useTranslation('onboarding-page');
  const reviews = useReviews();
  const spontaneousApplyData = useAtomValue(spontaneousApplyDataAtom);
  const isSpontaneousApplication = spontaneousApplyData?.isSpontaneousEntry;

  return (
    <div className="flex flex-col gap-9 pt-5">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <Heading size={2} testId="entry-point-title">
            {t(
              isSpontaneousApplication
                ? 'how_would_you_like_to_send_your_spontaneous_application'
                : 'how_do_you_want_to_apply'
            )}
          </Heading>
          <Text>
            {t(
              isSpontaneousApplication
                ? 'your_application_will_be_forwarded_key'
                : 'restworlds_commitment_is_to_help_thousands_of_people'
            )}
          </Text>
        </div>
        <ConfidentProjectionTextList />
      </div>
      <GoogleRestworldRatingUI {...reviews} />
    </div>
  );
});
const useReviews = () => {
  const [restworldGoogleRatingData, setRestworldGoogleRatingData] = useAtom(restworldGoogleRatingDataAtom);

  const [rating, setRating] = React.useState<number>(5);
  const [userRatingsTotal, setUserRatingsTotal] = React.useState<number>();

  const globalsService = useGlobalsService();

  const restworldRatingApi = React.useCallback(() => {
    globalsService
      .fetchRestworldRating()
      .then((res) => {
        setRestworldGoogleRatingData(res.data);
        setRating(res.data.rating);
        setUserRatingsTotal(res.data.user_ratings_total);
      })
      .catch((err) => console.error(err, 'error on getting reviews'));
  }, [globalsService, setRestworldGoogleRatingData]);

  const getRestworldRating = React.useCallback(() => {
    if (restworldGoogleRatingData && Object.keys(restworldGoogleRatingData)?.length) {
      const { rating, user_ratings_total } = restworldGoogleRatingData;
      setRating(rating);
      setUserRatingsTotal(user_ratings_total);
    } else restworldRatingApi();
  }, [restworldGoogleRatingData, restworldRatingApi]);

  React.useEffect(() => getRestworldRating(), [getRestworldRating]);

  return { userRatingsTotal, rating };
};

const ConfidentProjectionTextList = memo(() => {
  const { t } = useTranslation('onboarding-page');
  const spontaneousApplyData = useAtomValue(spontaneousApplyDataAtom);
  const isSpontaneousApplication = spontaneousApplyData?.isSpontaneousEntry;

  return (
    <ul className="flex flex-col gap-4">
      {[
        {
          iconJSX: <ChatBubbleLeftRightIcon className="h-6 w-6 text-primary-500" />,
          label: t('support_before_and_during_the_application'),
        },
        {
          iconJSX: <LockClosedIcon className="h-6 w-6 text-primary-500" />,
          label: t('personal_data_safe'),
          showForSpontaneousApply: true,
        },
        {
          iconJSX: <HouseCheckIcon />,
          label: t('only_venues_verified_by_our_managers'),
        },
        {
          iconJSX: <ShieldCheckIcon className="h-6 w-6 text-primary-500" />,
          label: t('no_commercial_communication_with_your_data'),
          showForSpontaneousApply: true,
        },
      ]
        .filter((v) => {
          if (!isSpontaneousApplication) return true;
          else return !!v.showForSpontaneousApply;
        })
        .map((v) => (
          <li key={v.label}>
            <span className="flex flex-row items-center gap-1.5">
              {v.iconJSX}
              {/* size=5 -> text-base, matching master's Title fontSize="base"; Heading defaults
                  to size 4 (text-xl), which made these list items too large. */}
              <Heading size={5} weight="medium">
                {v.label}
              </Heading>
            </span>
          </li>
        ))}
    </ul>
  );
});
export const EntryProcessButtons = memo(() => {
  const setActiveTab = useSetAtom(activeTabAtom);
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('onboarding-page');
  const jpData = useAtomValue(jobPositionApiDataAtom);
  const router = useRouter();

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <Button
        fullwidth
        variant="primary"
        onClick={() => {
          setActiveTab('upload-cv');
          trackEvent('initiate_upload_cv', {});
        }}
        testId="upload-cv-button"
      >
        {t('upload_a_cv')} <span className="font-light">(45 sec)</span>
      </Button>

      {/* If the ATS supports only redirect, no possibility to answer the questionaire  */}
      {!jpData.published_from_ats && (
        <Button
          fullwidth
          className="text-black"
          variant="secondary-neutral"
          onClick={() => {
            setActiveTab('application-wizard');
          }}
          testId="application-wizard-button"
        >
          {t('answer_the_questionnaire')} <span className="font-light">(5 min)</span>
        </Button>
      )}

      {/* `newTab={false}` so the login opens in this tab: the whole point of the fix is that the
          intent travels with the person, and a second tab leaves the original one stranded.
          `size="md"` for the 40px tap target (size="sm" is a fixed h-8, below the 36px this
          screen was already measured at); `text-sm` keeps the label quieter than the two CTAs. */}
      <Link
        href={buildLoginUrlWithApplyIntent(router.asPath)}
        newTab={false}
        size="md"
        variant="restworld"
        className="text-sm underline"
        data-testid="entry-point-login-link"
        onClick={() =>
          trackEvent(
            'existing_account_login_clicked',
            // No `methodHint`: this link is offered before the CV is read, so nothing is known
            // yet about which credential the person will use. That null is the point of the
            // property, and it is what separates this placement from the other two.
            loginClickedProperties({ jobPositionId: jpData?.id, placement: 'pre_cv' })
          )
        }
      >
        {t('already_have_an_account_log_in')}
      </Link>
    </div>
  );
});
// Renders just the title now — the back button lives in the ModalContent header (next to the X),
// and its reset is handled by the modal's onBack. `hideBackBtn` is kept in the type for callers.
export const TitleWithBackBtn = memo(({ title, testId }: { title: string; testId?: string; hideBackBtn?: boolean }) => (
  <Heading size={3} align="center" testId={testId}>
    {title}
  </Heading>
));
export const FadeInOutTransaction = ({ children, show }: { children: ReactNode; show: boolean }) => (
  <Transition
    show={show}
    enter="transition-opacity duration-175"
    enterFrom="opacity-0"
    enterTo="opacity-100"
    leave="transition-opacity duration-250"
    leaveFrom="opacity-100"
    leaveTo="opacity-0"
  >
    {(ref) => <div ref={ref as unknown as React.Ref<HTMLDivElement>}>{children}</div>}
  </Transition>
);
export * from './entry-component.context';
